import { ArrowLeft, Building2, Menu } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useRetailerWorkspace } from './RetailerWorkspaceContext';
import { SectionContent } from './SectionContent';
import {
  DEFAULT_RETAILER_SECTION,
  RETAILER_NAV_CATEGORIES,
  RETAILER_SECTIONS,
  getRetailerSection,
  sectionsForCategory,
  type RetailerSectionId,
} from './sections';
import { useState } from 'react';

interface RetailerWorkspaceShellProps {
  onEditRetailer: () => void;
  onArchiveRetailer: () => void;
  accessNotice?: string | null;
  onDismissAccessNotice?: () => void;
}

function WorkspaceNav({
  activeSection,
  onSelectSection,
  className,
}: {
  activeSection: RetailerSectionId;
  onSelectSection: (section: RetailerSectionId) => void;
  className?: string;
}) {
  return (
    <nav className={cn('space-y-6', className)}>
      {RETAILER_NAV_CATEGORIES.map((category) => {
        const sections = sectionsForCategory(category.id);
        if (sections.length === 0) return null;
        return (
          <div key={category.id}>
            <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {category.label}
            </p>
            <ul className="space-y-1">
              {sections.map((section) => {
                const isActive = activeSection === section.id;
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => onSelectSection(section.id)}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-sm transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted',
                      )}
                    >
                      {section.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

export function RetailerWorkspaceShell({
  onEditRetailer,
  onArchiveRetailer,
  accessNotice,
  onDismissAccessNotice,
}: RetailerWorkspaceShellProps) {
  const {
    retailers,
    activeRetailer,
    activeSection,
    selectRetailer,
    selectSection,
    clearRetailer,
  } = useRetailerWorkspace();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (!activeRetailer) {
    return (
      <div className="rounded-lg border border-dashed p-12 text-center text-muted-foreground">
        Retailer not found. Return to the directory and choose another retailer.
        <div className="mt-4">
          <Button variant="outline" onClick={clearRetailer}>
            Back to retailers
          </Button>
        </div>
      </div>
    );
  }

  const sectionMeta = getRetailerSection(activeSection);
  const sectionLabel = sectionMeta?.label ?? DEFAULT_RETAILER_SECTION;
  const sectionDescription = sectionMeta?.description ?? '';

  const handleSelectSection = (section: RetailerSectionId) => {
    selectSection(section);
    setMobileNavOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            <Button variant="ghost" size="sm" className="-ml-2 w-fit" onClick={clearRetailer}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              All retailers
            </Button>
            <div className="flex items-start gap-3">
              {activeRetailer.logo ? (
                <img
                  src={activeRetailer.logo}
                  alt={`${activeRetailer.name} logo`}
                  className="h-12 w-12 rounded border bg-white object-contain p-1"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded border bg-muted">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="truncate text-3xl font-bold text-gray-900">
                  {activeRetailer.name}
                </h1>
                <p className="text-gray-500">Retailer workspace</p>
                {activeRetailer.archivedAt && (
                  <div className="mt-2">
                    <Badge
                      variant="destructive"
                      className="border-amber-300 bg-amber-100 text-amber-800"
                    >
                      Archived
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-2 sm:max-w-xs">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Switch retailer
            </label>
            <Select
              value={String(activeRetailer.id)}
              onValueChange={(value) => selectRetailer(Number(value), activeSection)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select retailer" />
              </SelectTrigger>
              <SelectContent>
                {retailers.map((retailer) => (
                  <SelectItem key={retailer.id} value={String(retailer.id)}>
                    {retailer.name}
                    {retailer.archivedAt ? ' (Archived)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEditRetailer}>
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={onArchiveRetailer}>
                {activeRetailer.archivedAt ? 'Restore' : 'Archive'}
              </Button>
            </div>
          </div>
        </div>

        {accessNotice && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="flex items-start justify-between gap-3">
              <p>{accessNotice}</p>
              {onDismissAccessNotice && (
                <button
                  type="button"
                  className="shrink-0 text-amber-800 underline"
                  onClick={onDismissAccessNotice}
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="lg:hidden">
        <div className="flex items-center gap-2">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <Menu className="mr-2 h-4 w-4" />
                Sections
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Workspace sections</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <WorkspaceNav
                  activeSection={activeSection}
                  onSelectSection={handleSelectSection}
                />
              </div>
            </SheetContent>
          </Sheet>
          <Select
            value={activeSection}
            onValueChange={(value) => handleSelectSection(value as RetailerSectionId)}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select section" />
            </SelectTrigger>
            <SelectContent>
              {RETAILER_SECTIONS.map((section) => (
                <SelectItem key={section.id} value={section.id}>
                  {section.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <div className="hidden rounded-lg border bg-background p-3 lg:sticky lg:top-4 lg:block lg:self-start">
          <WorkspaceNav
            activeSection={activeSection}
            onSelectSection={handleSelectSection}
          />
        </div>

        <div className="min-w-0">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">{sectionLabel}</h2>
            {sectionDescription && (
              <p className="text-sm text-muted-foreground">{sectionDescription}</p>
            )}
          </div>
          <SectionContent
            key={`${activeRetailer.id}:${activeSection}`}
            section={activeSection}
            onEditRetailer={onEditRetailer}
            onArchiveRetailer={onArchiveRetailer}
          />
        </div>
      </div>
    </div>
  );
}
